import { useState, useMemo, useEffect } from 'react'
import { useParams, useLocation, Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { getProject, getWafCafAudits, getWafCafAuditData, generateRemediation, WafCafAudit, WafCafAuditFile, WafCafAuditData, RemediationResponse } from '../services/api'
import {
  PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar
} from 'recharts'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import BacklogPanel, { AddToBacklogButton } from '../components/BacklogPanel'

const glassPanel = { background: 'rgba(15, 23, 42, 0.6)', backdropFilter: 'blur(20px)', border: '1px solid rgba(100, 180, 255, 0.1)' }

// Category icons and colors
const categoryStyles: Record<string, { icon: string; color: string; label: string }> = {
  // Findings-specific categories
  findings: { icon: 'fa-clipboard-list', color: '#f472b6', label: 'Findings Overview' },
  compliance: { icon: 'fa-check-circle', color: '#34d399', label: 'Compliance' },
  remediation: { icon: 'fa-wrench', color: '#fb923c', label: 'Remediation' },
  management_groups: { icon: 'fa-sitemap', color: '#a78bfa', label: 'Management Groups' },
  landing_zone: { icon: 'fa-building', color: '#60a5fa', label: 'Landing Zone' },
  resources: { icon: 'fa-cubes', color: '#38bdf8', label: 'Resources' },
  // Standard categories
  advisor: { icon: 'fa-user-tie', color: '#60a5fa', label: 'Azure Advisor' },
  cost: { icon: 'fa-dollar-sign', color: '#22c55e', label: 'Cost Management' },
  security: { icon: 'fa-shield-halved', color: '#f87171', label: 'Security' },
  identity: { icon: 'fa-id-card', color: '#a78bfa', label: 'Identity' },
  governance: { icon: 'fa-gavel', color: '#fbbf24', label: 'Governance' },
  network: { icon: 'fa-network-wired', color: '#14b8a6', label: 'Networking' },
  monitoring: { icon: 'fa-chart-line', color: '#f472b6', label: 'Monitoring' },
  disaster_recovery: { icon: 'fa-rotate', color: '#fb923c', label: 'Disaster Recovery' },
  compute: { icon: 'fa-server', color: '#38bdf8', label: 'Compute' },
  paas: { icon: 'fa-cloud', color: '#818cf8', label: 'PaaS & Storage' },
  subscriptions: { icon: 'fa-layer-group', color: '#34d399', label: 'Subscriptions' },
  other: { icon: 'fa-file-alt', color: '#94a3b8', label: 'Other' },
}

// Severity colors
const severityColors: Record<string, string> = {
  Critical: '#dc2626',
  High: '#f87171',
  Medium: '#fbbf24',
  Low: '#22c55e',
  Info: '#60a5fa',
}

// WAF Pillar colors
const pillarColors: Record<string, string> = {
  Security: '#f87171',
  Reliability: '#60a5fa',
  CostOptimization: '#22c55e',
  OperationalExcellence: '#fbbf24',
  Performance: '#a78bfa',
}

type RowData = Record<string, unknown>

// Drill-down state type
type DrillDownState = {
  type: 'severity' | 'category' | 'resource' | null
  value: string | null
}

// Finding type for drill-down
type Finding = {
  category: string
  resourceName: string
  finding: string
  severity: string
  recommendation: string
  source: string
  subscription: string
}

// Custom tooltip for charts
const CustomTooltip = ({ active, payload, label }: { active?: boolean; payload?: Array<{ name: string; value: number; color?: string }>; label?: string }) => {
  if (active && payload && payload.length) {
    return (
      <div className="px-3 py-2 rounded-lg" style={{ background: 'rgba(15, 23, 42, 0.95)', border: '1px solid rgba(100, 180, 255, 0.2)' }}>
        <p className="text-sm font-medium" style={{ color: '#e2e8f0' }}>{label}</p>
        {payload.map((entry, index) => (
          <p key={index} className="text-sm" style={{ color: entry.color || '#94a3b8' }}>
            {entry.name}: {entry.value}
          </p>
        ))}
      </div>
    )
  }
  return null
}

// Score Card Component
function ScoreCard({ title, score, color, icon }: { title: string; score: number; color: string; icon: string }) {
  const getScoreColor = (s: number) => {
    if (s >= 80) return '#22c55e'
    if (s >= 60) return '#fbbf24'
    return '#f87171'
  }

  return (
    <div className="rounded-xl p-4" style={glassPanel}>
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center space-x-2">
          <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: `${color}20`, border: `1px solid ${color}40` }}>
            <i className={`fa-solid ${icon}`} style={{ color, fontSize: '0.875rem' }}></i>
          </div>
          <span className="text-sm font-medium" style={{ color: 'rgba(148, 163, 184, 0.9)' }}>{title}</span>
        </div>
      </div>
      <div className="flex items-end justify-between">
        <span className="text-3xl font-bold" style={{ color: getScoreColor(score) }}>{score}</span>
        <span className="text-sm" style={{ color: 'rgba(148, 163, 184, 0.6)' }}>/100</span>
      </div>
      <div className="mt-2 h-2 rounded-full overflow-hidden" style={{ background: 'rgba(30, 41, 59, 0.8)' }}>
        <div className="h-full rounded-full transition-all duration-500" style={{ width: `${score}%`, background: getScoreColor(score) }}></div>
      </div>
    </div>
  )
}

// Findings Summary Component with click handler - compact version
function FindingsSummary({ findings, onSeverityClick }: { findings: { severity: string; count: number }[]; onSeverityClick: (severity: string) => void }) {
  const total = findings.reduce((sum, f) => sum + f.count, 0)

  return (
    <div className="rounded-xl p-4 h-full" style={glassPanel}>
      <h3 className="text-base font-bold mb-1" style={{ color: '#e2e8f0' }}>Findings by Severity</h3>
      <p className="text-xs mb-3" style={{ color: 'rgba(148, 163, 184, 0.5)' }}>Click to drill down</p>
      <div className="flex items-center gap-4">
        <div className="w-28 h-28 flex-shrink-0">
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={findings}
                cx="50%"
                cy="50%"
                innerRadius={25}
                outerRadius={45}
                paddingAngle={2}
                dataKey="count"
                nameKey="severity"
                onClick={(data) => onSeverityClick(data.severity)}
                style={{ cursor: 'pointer' }}
              >
                {findings.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={severityColors[entry.severity] || '#94a3b8'} />
                ))}
              </Pie>
              <Tooltip content={<CustomTooltip />} />
            </PieChart>
          </ResponsiveContainer>
        </div>
        <div className="flex-1 space-y-1">
          {findings.map((f) => (
            <button
              key={f.severity}
              onClick={() => onSeverityClick(f.severity)}
              className="w-full flex items-center justify-between py-1 px-1.5 rounded transition hover:bg-white/5"
            >
              <div className="flex items-center space-x-1.5">
                <div className="w-2 h-2 rounded-full" style={{ background: severityColors[f.severity] || '#94a3b8' }}></div>
                <span className="text-xs" style={{ color: 'rgba(148, 163, 184, 0.9)' }}>{f.severity}</span>
              </div>
              <div className="flex items-center space-x-1.5">
                <span className="text-xs font-semibold" style={{ color: '#e2e8f0' }}>{f.count}</span>
                <span className="text-xs" style={{ color: 'rgba(148, 163, 184, 0.4)' }}>({total > 0 ? Math.round((f.count / total) * 100) : 0}%)</span>
              </div>
            </button>
          ))}
          <div className="pt-1 mt-1" style={{ borderTop: '1px solid rgba(100, 180, 255, 0.1)' }}>
            <div className="flex items-center justify-between px-1.5">
              <span className="text-xs font-medium" style={{ color: 'rgba(148, 163, 184, 0.9)' }}>Total</span>
              <span className="text-xs font-bold" style={{ color: '#e2e8f0' }}>{total}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

// Resource Type Chart Component with click handler
function ResourceTypeChart({ data, onResourceClick }: { data: { type: string; count: number }[]; onResourceClick: (type: string) => void }) {
  const topResources = data.slice(0, 10)

  return (
    <div className="rounded-xl p-6" style={glassPanel}>
      <h3 className="text-lg font-bold mb-2" style={{ color: '#e2e8f0' }}>Top Resource Types</h3>
      <p className="text-xs mb-4" style={{ color: 'rgba(148, 163, 184, 0.5)' }}>Click on a bar to see resources</p>
      <div className="h-64">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={topResources} layout="vertical" margin={{ top: 5, right: 30, left: 100, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(100, 180, 255, 0.1)" />
            <XAxis type="number" stroke="rgba(148, 163, 184, 0.5)" fontSize={12} />
            <YAxis type="category" dataKey="type" stroke="rgba(148, 163, 184, 0.5)" fontSize={11} width={95} tick={{ fill: 'rgba(148, 163, 184, 0.8)' }} />
            <Tooltip content={<CustomTooltip />} />
            <Bar dataKey="count" fill="#60a5fa" radius={[0, 4, 4, 0]} onClick={(data) => data.type && onResourceClick(data.type)} style={{ cursor: 'pointer' }} />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  )
}

// WAF Radar Chart Component
function WafRadarChart({ scores }: { scores: { pillar: string; score: number; fullMark: number }[] }) {
  return (
    <div className="rounded-xl p-6" style={glassPanel}>
      <h3 className="text-lg font-bold mb-4" style={{ color: '#e2e8f0' }}>WAF Pillar Assessment</h3>
      <div className="h-72">
        <ResponsiveContainer width="100%" height="100%">
          <RadarChart cx="50%" cy="50%" outerRadius="70%" data={scores}>
            <PolarGrid stroke="rgba(100, 180, 255, 0.2)" />
            <PolarAngleAxis dataKey="pillar" tick={{ fill: 'rgba(148, 163, 184, 0.8)', fontSize: 11 }} />
            <PolarRadiusAxis angle={90} domain={[0, 100]} tick={{ fill: 'rgba(148, 163, 184, 0.5)', fontSize: 10 }} />
            <Radar name="Score" dataKey="score" stroke="#60a5fa" fill="#60a5fa" fillOpacity={0.3} />
            <Tooltip content={<CustomTooltip />} />
          </RadarChart>
        </ResponsiveContainer>
      </div>
    </div>
  )
}

// Category Stats Card - clickable
function CategoryStatsCard({ category, files, color, icon, onClick }: { category: string; files: WafCafAuditFile[]; color: string; icon: string; onClick: () => void }) {
  const totalRows = files.reduce((sum, f) => sum + f.row_count, 0)
  const findingsFiles = files.filter(f => f.name.includes('findings'))

  return (
    <button onClick={onClick} className="rounded-xl p-4 transition hover:scale-[1.02] text-left w-full" style={glassPanel}>
      <div className="flex items-center space-x-3 mb-3">
        <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ background: `${color}20`, border: `1px solid ${color}40` }}>
          <i className={`fa-solid ${icon}`} style={{ color, fontSize: '1rem' }}></i>
        </div>
        <div className="flex-1">
          <h4 className="font-semibold" style={{ color: '#e2e8f0' }}>{categoryStyles[category]?.label || category}</h4>
          <p className="text-xs" style={{ color: 'rgba(148, 163, 184, 0.6)' }}>{files.length} files</p>
        </div>
        <i className="fa-solid fa-chevron-right" style={{ color: 'rgba(148, 163, 184, 0.4)' }}></i>
      </div>
      <div className="grid grid-cols-2 gap-2 text-sm">
        <div className="px-2 py-1 rounded" style={{ background: 'rgba(30, 41, 59, 0.5)' }}>
          <p className="text-xs" style={{ color: 'rgba(148, 163, 184, 0.5)' }}>Records</p>
          <p className="font-semibold" style={{ color: '#e2e8f0' }}>{totalRows.toLocaleString()}</p>
        </div>
        {findingsFiles.length > 0 && (
          <div className="px-2 py-1 rounded" style={{ background: 'rgba(30, 41, 59, 0.5)' }}>
            <p className="text-xs" style={{ color: 'rgba(148, 163, 184, 0.5)' }}>Findings</p>
            <p className="font-semibold" style={{ color: '#fbbf24' }}>{findingsFiles.length}</p>
          </div>
        )}
      </div>
    </button>
  )
}

// Remediation Panel Component
function RemediationPanel({
  remediation,
  isLoading,
  error,
  onClose
}: {
  remediation: RemediationResponse | null
  isLoading: boolean
  error: string | null
  onClose: () => void
}) {
  const [copiedSnippet, setCopiedSnippet] = useState<string | null>(null)

  const copyToClipboard = (text: string, type: string) => {
    navigator.clipboard.writeText(text)
    setCopiedSnippet(type)
    setTimeout(() => setCopiedSnippet(null), 2000)
  }

  if (isLoading) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center" style={{ background: 'rgba(0, 0, 0, 0.7)' }}>
        <div className="rounded-xl p-8 max-w-2xl w-full mx-4 text-center" style={glassPanel}>
          <i className="fa-solid fa-spinner fa-spin text-4xl mb-4" style={{ color: '#60a5fa' }}></i>
          <h3 className="text-lg font-semibold mb-2" style={{ color: '#e2e8f0' }}>Generating Remediation Steps</h3>
          <p className="text-sm" style={{ color: 'rgba(148, 163, 184, 0.7)' }}>Analyzing finding and searching best practices...</p>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center" style={{ background: 'rgba(0, 0, 0, 0.7)' }}>
        <div className="rounded-xl p-6 max-w-lg w-full mx-4" style={glassPanel}>
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold" style={{ color: '#f87171' }}>Error</h3>
            <button onClick={onClose} className="p-2 rounded-lg hover:bg-white/10"><i className="fa-solid fa-times" style={{ color: 'rgba(148, 163, 184, 0.8)' }}></i></button>
          </div>
          <p className="text-sm" style={{ color: 'rgba(148, 163, 184, 0.9)' }}>{error}</p>
        </div>
      </div>
    )
  }

  if (!remediation) return null

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto py-8" style={{ background: 'rgba(0, 0, 0, 0.7)' }}>
      <div className="rounded-xl max-w-4xl w-full mx-4 my-auto" style={glassPanel}>
        {/* Header */}
        <div className="px-6 py-4 flex items-center justify-between" style={{ borderBottom: '1px solid rgba(100, 180, 255, 0.1)' }}>
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ background: 'rgba(34, 197, 94, 0.2)', border: '1px solid rgba(34, 197, 94, 0.4)' }}>
              <i className="fa-solid fa-wrench" style={{ color: '#22c55e' }}></i>
            </div>
            <div>
              <h3 className="text-lg font-semibold" style={{ color: '#e2e8f0' }}>Remediation Steps</h3>
              <p className="text-xs" style={{ color: 'rgba(148, 163, 184, 0.6)' }}>
                {remediation.category} • {remediation.severity} severity
                {remediation.estimated_effort && ` • ${remediation.estimated_effort}`}
              </p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-white/10 transition">
            <i className="fa-solid fa-times text-lg" style={{ color: 'rgba(148, 163, 184, 0.8)' }}></i>
          </button>
        </div>

        {/* Content */}
        <div className="p-6 max-h-[70vh] overflow-y-auto">
          {/* Remediation Steps - Markdown */}
          <div className="prose prose-invert max-w-none mb-6">
            <ReactMarkdown
              remarkPlugins={[remarkGfm]}
              components={{
                h1: ({ children }) => <h1 className="text-xl font-bold mb-4" style={{ color: '#e2e8f0' }}>{children}</h1>,
                h2: ({ children }) => <h2 className="text-lg font-bold mb-3 mt-6" style={{ color: '#e2e8f0' }}>{children}</h2>,
                h3: ({ children }) => <h3 className="text-base font-semibold mb-2 mt-4" style={{ color: '#e2e8f0' }}>{children}</h3>,
                p: ({ children }) => <p className="mb-3 text-sm leading-relaxed" style={{ color: 'rgba(148, 163, 184, 0.9)' }}>{children}</p>,
                ul: ({ children }) => <ul className="list-disc list-inside mb-3 space-y-1">{children}</ul>,
                ol: ({ children }) => <ol className="list-decimal list-inside mb-3 space-y-1">{children}</ol>,
                li: ({ children }) => <li className="text-sm" style={{ color: 'rgba(148, 163, 184, 0.9)' }}>{children}</li>,
                code: ({ children, className }) => {
                  const isInline = !className
                  return isInline ? (
                    <code className="px-1.5 py-0.5 rounded text-xs" style={{ background: 'rgba(30, 41, 59, 0.8)', color: '#f472b6' }}>{children}</code>
                  ) : (
                    <code className="block p-3 rounded-lg text-xs overflow-x-auto" style={{ background: 'rgba(15, 23, 42, 0.8)', color: 'rgba(148, 163, 184, 0.9)' }}>{children}</code>
                  )
                },
                pre: ({ children }) => <pre className="mb-3 overflow-x-auto">{children}</pre>,
                table: ({ children }) => <div className="overflow-x-auto mb-4"><table className="w-full text-sm" style={{ borderCollapse: 'collapse' }}>{children}</table></div>,
                thead: ({ children }) => <thead style={{ background: 'rgba(30, 41, 59, 0.8)' }}>{children}</thead>,
                th: ({ children }) => <th className="px-3 py-2 text-left font-semibold" style={{ color: '#e2e8f0', border: '1px solid rgba(100, 180, 255, 0.2)' }}>{children}</th>,
                td: ({ children }) => <td className="px-3 py-2" style={{ color: 'rgba(148, 163, 184, 0.9)', border: '1px solid rgba(100, 180, 255, 0.1)' }}>{children}</td>,
                strong: ({ children }) => <strong style={{ color: '#e2e8f0' }}>{children}</strong>,
              }}
            >
              {remediation.remediation_steps}
            </ReactMarkdown>
          </div>

          {/* Code Snippets - Easy Copy */}
          {remediation.code_snippets && Object.keys(remediation.code_snippets).length > 0 && (
            <div className="space-y-4">
              <h4 className="text-sm font-semibold" style={{ color: '#e2e8f0' }}>Quick Copy Code Snippets</h4>
              {remediation.code_snippets.azure_cli && (
                <div className="rounded-lg overflow-hidden" style={{ border: '1px solid rgba(100, 180, 255, 0.2)' }}>
                  <div className="px-4 py-2 flex items-center justify-between" style={{ background: 'rgba(30, 41, 59, 0.8)' }}>
                    <span className="text-xs font-medium" style={{ color: '#60a5fa' }}>Azure CLI</span>
                    <button
                      onClick={() => copyToClipboard(remediation.code_snippets!.azure_cli!, 'azure_cli')}
                      className="text-xs px-2 py-1 rounded transition hover:bg-white/10"
                      style={{ color: copiedSnippet === 'azure_cli' ? '#22c55e' : 'rgba(148, 163, 184, 0.8)' }}
                    >
                      <i className={`fa-solid ${copiedSnippet === 'azure_cli' ? 'fa-check' : 'fa-copy'} mr-1`}></i>
                      {copiedSnippet === 'azure_cli' ? 'Copied!' : 'Copy'}
                    </button>
                  </div>
                  <pre className="p-4 text-xs overflow-x-auto" style={{ background: 'rgba(15, 23, 42, 0.6)', color: 'rgba(148, 163, 184, 0.9)' }}>
                    {remediation.code_snippets.azure_cli}
                  </pre>
                </div>
              )}
              {remediation.code_snippets.powershell && (
                <div className="rounded-lg overflow-hidden" style={{ border: '1px solid rgba(167, 139, 250, 0.2)' }}>
                  <div className="px-4 py-2 flex items-center justify-between" style={{ background: 'rgba(30, 41, 59, 0.8)' }}>
                    <span className="text-xs font-medium" style={{ color: '#a78bfa' }}>PowerShell</span>
                    <button
                      onClick={() => copyToClipboard(remediation.code_snippets!.powershell!, 'powershell')}
                      className="text-xs px-2 py-1 rounded transition hover:bg-white/10"
                      style={{ color: copiedSnippet === 'powershell' ? '#22c55e' : 'rgba(148, 163, 184, 0.8)' }}
                    >
                      <i className={`fa-solid ${copiedSnippet === 'powershell' ? 'fa-check' : 'fa-copy'} mr-1`}></i>
                      {copiedSnippet === 'powershell' ? 'Copied!' : 'Copy'}
                    </button>
                  </div>
                  <pre className="p-4 text-xs overflow-x-auto" style={{ background: 'rgba(15, 23, 42, 0.6)', color: 'rgba(148, 163, 184, 0.9)' }}>
                    {remediation.code_snippets.powershell}
                  </pre>
                </div>
              )}
              {remediation.code_snippets.terraform && (
                <div className="rounded-lg overflow-hidden" style={{ border: '1px solid rgba(139, 92, 246, 0.2)' }}>
                  <div className="px-4 py-2 flex items-center justify-between" style={{ background: 'rgba(30, 41, 59, 0.8)' }}>
                    <span className="text-xs font-medium" style={{ color: '#8b5cf6' }}>Terraform</span>
                    <button
                      onClick={() => copyToClipboard(remediation.code_snippets!.terraform!, 'terraform')}
                      className="text-xs px-2 py-1 rounded transition hover:bg-white/10"
                      style={{ color: copiedSnippet === 'terraform' ? '#22c55e' : 'rgba(148, 163, 184, 0.8)' }}
                    >
                      <i className={`fa-solid ${copiedSnippet === 'terraform' ? 'fa-check' : 'fa-copy'} mr-1`}></i>
                      {copiedSnippet === 'terraform' ? 'Copied!' : 'Copy'}
                    </button>
                  </div>
                  <pre className="p-4 text-xs overflow-x-auto" style={{ background: 'rgba(15, 23, 42, 0.6)', color: 'rgba(148, 163, 184, 0.9)' }}>
                    {remediation.code_snippets.terraform}
                  </pre>
                </div>
              )}
            </div>
          )}

          {/* Best Practice Sources */}
          {remediation.best_practice_sources.length > 0 && (
            <div className="mt-6 p-4 rounded-lg" style={{ background: 'rgba(30, 41, 59, 0.5)' }}>
              <h4 className="text-xs font-semibold mb-2" style={{ color: 'rgba(148, 163, 184, 0.7)' }}>Referenced Best Practices</h4>
              <div className="flex flex-wrap gap-2">
                {remediation.best_practice_sources.map((source, idx) => (
                  <span key={idx} className="text-xs px-2 py-1 rounded" style={{ background: 'rgba(96, 165, 250, 0.1)', color: '#60a5fa' }}>
                    {source}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

// Findings Detail View - shows all findings filtered by severity
function FindingsDetailView({ findings, severityFilter, projectId, auditId, onBack, onClearFilter }: { findings: Finding[]; severityFilter: string | null; projectId: string; auditId: string; onBack: () => void; onClearFilter: () => void }) {
  const [searchTerm, setSearchTerm] = useState('')
  const [localSeverity, setLocalSeverity] = useState<string | null>(severityFilter)
  const [remediationLoading, setRemediationLoading] = useState(false)
  const [remediationError, setRemediationError] = useState<string | null>(null)
  const [remediation, setRemediation] = useState<RemediationResponse | null>(null)

  const filteredFindings = findings.filter(f => {
    const matchesSeverity = !localSeverity || f.severity === localSeverity
    const matchesSearch = !searchTerm ||
      f.finding.toLowerCase().includes(searchTerm.toLowerCase()) ||
      f.resourceName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      f.category.toLowerCase().includes(searchTerm.toLowerCase()) ||
      f.recommendation.toLowerCase().includes(searchTerm.toLowerCase())
    return matchesSeverity && matchesSearch
  })

  const severityCounts = findings.reduce((acc, f) => {
    acc[f.severity] = (acc[f.severity] || 0) + 1
    return acc
  }, {} as Record<string, number>)

  const handleGetFix = async (finding: Finding) => {
    setRemediationLoading(true)
    setRemediationError(null)
    setRemediation(null)

    try {
      const response = await generateRemediation(auditId, {
        finding: finding.finding,
        severity: finding.severity,
        category: finding.category,
        resource_name: finding.resourceName,
        recommendation: finding.recommendation,
        subscription: finding.subscription,
      })
      setRemediation(response.data)
    } catch (err: unknown) {
      const error = err as { response?: { data?: { detail?: string } }; message?: string }
      setRemediationError(error.response?.data?.detail || error.message || 'Failed to generate remediation steps')
    } finally {
      setRemediationLoading(false)
    }
  }

  const closeRemediation = () => {
    setRemediation(null)
    setRemediationError(null)
  }

  return (
    <div className="space-y-4">
      {/* Remediation Panel */}
      {(remediationLoading || remediationError || remediation) && (
        <RemediationPanel
          remediation={remediation}
          isLoading={remediationLoading}
          error={remediationError}
          onClose={closeRemediation}
        />
      )}

      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <button onClick={onBack} className="p-2 rounded-lg transition hover:bg-white/10" style={{ color: '#60a5fa' }}>
            <i className="fa-solid fa-arrow-left"></i>
          </button>
          <div>
            <h2 className="text-xl font-bold" style={{ color: '#e2e8f0' }}>
              {localSeverity ? `${localSeverity} Findings` : 'All Findings'}
            </h2>
            <p className="text-sm" style={{ color: 'rgba(148, 163, 184, 0.6)' }}>
              {filteredFindings.length} of {findings.length} findings
            </p>
          </div>
        </div>
        {localSeverity && (
          <button onClick={() => { setLocalSeverity(null); onClearFilter(); }} className="px-3 py-1.5 rounded-lg text-sm transition hover:bg-white/10" style={{ color: 'rgba(148, 163, 184, 0.8)', border: '1px solid rgba(100, 180, 255, 0.2)' }}>
            <i className="fa-solid fa-times mr-2"></i>Clear Filter
          </button>
        )}
      </div>

      {/* Filters */}
      <div className="rounded-xl p-4" style={glassPanel}>
        <div className="flex flex-wrap items-center gap-4">
          <input
            type="text"
            placeholder="Search findings..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="flex-1 min-w-[200px] px-4 py-2 rounded-lg text-sm"
            style={{ background: 'rgba(15, 23, 42, 0.6)', border: '1px solid rgba(100, 180, 255, 0.2)', color: '#e2e8f0' }}
          />
          <div className="flex items-center gap-2">
            {['Critical', 'High', 'Medium', 'Low', 'Info'].map(sev => (
              <button
                key={sev}
                onClick={() => setLocalSeverity(localSeverity === sev ? null : sev)}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium transition ${localSeverity === sev ? 'ring-2 ring-offset-1 ring-offset-transparent' : ''}`}
                style={{
                  background: localSeverity === sev ? `${severityColors[sev]}30` : 'rgba(30, 41, 59, 0.5)',
                  color: severityColors[sev],
                  border: `1px solid ${severityColors[sev]}50`,
                }}
              >
                {sev} ({severityCounts[sev] || 0})
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Findings List - grouped by finding description */}
      <div className="space-y-3">
        {filteredFindings.length === 0 ? (
          <div className="rounded-xl p-8 text-center" style={glassPanel}>
            <i className="fa-solid fa-search text-4xl mb-4" style={{ color: 'rgba(148, 163, 184, 0.3)' }}></i>
            <p style={{ color: 'rgba(148, 163, 184, 0.7)' }}>No findings match your criteria</p>
          </div>
        ) : (
          // Group findings by finding description + severity + category
          (() => {
            const grouped = filteredFindings.reduce((acc, finding) => {
              const key = `${finding.finding}|${finding.severity}|${finding.category}`
              if (!acc[key]) {
                acc[key] = { ...finding, resources: [] as string[], subscriptions: new Set<string>() }
              }
              if (finding.resourceName && finding.resourceName !== 'Unknown') {
                acc[key].resources.push(finding.resourceName)
              }
              if (finding.subscription) {
                acc[key].subscriptions.add(finding.subscription)
              }
              return acc
            }, {} as Record<string, Finding & { resources: string[]; subscriptions: Set<string> }>)

            return Object.values(grouped).map((finding, idx) => (
              <div key={idx} className="rounded-xl p-4" style={glassPanel}>
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center space-x-3">
                    <span
                      className="px-2.5 py-1 rounded-full text-xs font-semibold"
                      style={{ background: `${severityColors[finding.severity]}20`, color: severityColors[finding.severity], border: `1px solid ${severityColors[finding.severity]}40` }}
                    >
                      {finding.severity}
                    </span>
                    <span className="text-xs px-2 py-1 rounded" style={{ background: 'rgba(30, 41, 59, 0.8)', color: 'rgba(148, 163, 184, 0.8)' }}>
                      {categoryStyles[finding.category]?.label || finding.category}
                    </span>
                    {finding.resources.length > 1 && (
                      <span className="text-xs px-2 py-1 rounded font-semibold" style={{ background: 'rgba(251, 191, 36, 0.2)', color: '#fbbf24', border: '1px solid rgba(251, 191, 36, 0.3)' }}>
                        {finding.resources.length} resources
                      </span>
                    )}
                  </div>
                  <span className="text-xs" style={{ color: 'rgba(148, 163, 184, 0.5)' }}>
                    {finding.subscriptions.size > 1 ? `${finding.subscriptions.size} subscriptions` : Array.from(finding.subscriptions)[0] || ''}
                  </span>
                </div>
                <p className="text-sm font-medium mb-2" style={{ color: '#e2e8f0' }}>{finding.finding}</p>

                {/* Show affected resources list */}
                {finding.resources.length > 0 && (
                  <div className="mb-3 p-2 rounded-lg" style={{ background: 'rgba(15, 23, 42, 0.5)', border: '1px solid rgba(100, 180, 255, 0.1)' }}>
                    <p className="text-xs font-medium mb-1.5" style={{ color: 'rgba(148, 163, 184, 0.6)' }}>
                      Affected Resources ({finding.resources.length})
                    </p>
                    <div className="flex flex-wrap gap-1.5">
                      {finding.resources.slice(0, 20).map((resource, rIdx) => (
                        <span key={rIdx} className="text-xs px-2 py-0.5 rounded" style={{ background: 'rgba(30, 41, 59, 0.8)', color: 'rgba(148, 163, 184, 0.9)' }}>
                          {resource}
                        </span>
                      ))}
                      {finding.resources.length > 20 && (
                        <span className="text-xs px-2 py-0.5 rounded" style={{ background: 'rgba(96, 165, 250, 0.2)', color: '#60a5fa' }}>
                          +{finding.resources.length - 20} more
                        </span>
                      )}
                    </div>
                  </div>
                )}

                {finding.recommendation && (
                  <div className="p-3 rounded-lg mb-3" style={{ background: 'rgba(96, 165, 250, 0.1)', border: '1px solid rgba(96, 165, 250, 0.2)' }}>
                    <p className="text-xs font-medium mb-1" style={{ color: '#60a5fa' }}>Recommendation</p>
                    <p className="text-sm" style={{ color: 'rgba(148, 163, 184, 0.9)' }}>{finding.recommendation}</p>
                  </div>
                )}
                <div className="flex items-center justify-between">
                  <p className="text-xs" style={{ color: 'rgba(148, 163, 184, 0.4)' }}>Source: {finding.source}</p>
                  <div className="flex items-center gap-2">
                  <AddToBacklogButton
                    projectId={projectId}
                    auditId={auditId}
                    finding={finding.finding}
                    category={finding.category}
                    severity={finding.severity}
                    resourceName={finding.resourceName}
                    subscription={finding.subscription}
                    recommendation={finding.recommendation}
                    sourceFile={finding.source}
                  />
                  <button
                    onClick={() => handleGetFix(finding)}
                    className="px-3 py-1.5 rounded-lg text-sm font-medium transition hover:scale-105"
                    style={{ background: 'rgba(34, 197, 94, 0.2)', border: '1px solid rgba(34, 197, 94, 0.4)', color: '#22c55e' }}
                  >
                    <i className="fa-solid fa-wrench mr-2"></i>Get Fix
                  </button>
                </div>
              </div>
            </div>
            ))
          })()
        )}
      </div>
    </div>
  )
}

// Resource Detail View - shows resources of a specific type
function ResourceDetailView({ files, resourceType, onBack }: { files: WafCafAuditFile[]; resourceType: string; onBack: () => void }) {
  const [searchTerm, setSearchTerm] = useState('')

  // Find resources matching the type
  const matchingResources: RowData[] = []
  files.forEach(file => {
    if (file.name.includes('resource-inventory') || file.name.includes('vm-inventory') || file.name.includes('resource-type')) {
      // Skip markdown files
      if (typeof file.data === 'string') return
      const rows = Array.isArray(file.data) ? file.data : [file.data]
      rows.forEach((row: RowData) => {
        const type = String(row['ResourceType'] || row['Type'] || '')
        if (type.toLowerCase().includes(resourceType.toLowerCase()) || type.split('/').pop()?.toLowerCase() === resourceType.toLowerCase()) {
          matchingResources.push({ ...row, _source: file.name, _subscription: file.subscription })
        }
      })
    }
  })

  const filteredResources = searchTerm
    ? matchingResources.filter(r =>
        Object.values(r).some(v => String(v).toLowerCase().includes(searchTerm.toLowerCase()))
      )
    : matchingResources

  const columns = matchingResources.length > 0
    ? Object.keys(matchingResources[0]).filter(k => !k.startsWith('_'))
    : []

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center space-x-3">
        <button onClick={onBack} className="p-2 rounded-lg transition hover:bg-white/10" style={{ color: '#60a5fa' }}>
          <i className="fa-solid fa-arrow-left"></i>
        </button>
        <div>
          <h2 className="text-xl font-bold" style={{ color: '#e2e8f0' }}>{resourceType} Resources</h2>
          <p className="text-sm" style={{ color: 'rgba(148, 163, 184, 0.6)' }}>{filteredResources.length} resources found</p>
        </div>
      </div>

      {/* Search */}
      <div className="rounded-xl p-4" style={glassPanel}>
        <input
          type="text"
          placeholder="Search resources..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full px-4 py-2 rounded-lg text-sm"
          style={{ background: 'rgba(15, 23, 42, 0.6)', border: '1px solid rgba(100, 180, 255, 0.2)', color: '#e2e8f0' }}
        />
      </div>

      {/* Table */}
      <div className="rounded-xl overflow-hidden" style={glassPanel}>
        {filteredResources.length === 0 ? (
          <div className="p-8 text-center">
            <i className="fa-solid fa-cube text-4xl mb-4" style={{ color: 'rgba(148, 163, 184, 0.3)' }}></i>
            <p style={{ color: 'rgba(148, 163, 184, 0.7)' }}>No resources found</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr style={{ background: 'rgba(30, 41, 59, 0.8)' }}>
                  {columns.slice(0, 6).map(col => (
                    <th key={col} className="px-4 py-3 text-left font-semibold whitespace-nowrap" style={{ color: '#e2e8f0', borderBottom: '1px solid rgba(100, 180, 255, 0.1)' }}>
                      {col}
                    </th>
                  ))}
                  <th className="px-4 py-3 text-left font-semibold whitespace-nowrap" style={{ color: '#e2e8f0', borderBottom: '1px solid rgba(100, 180, 255, 0.1)' }}>
                    Subscription
                  </th>
                </tr>
              </thead>
              <tbody>
                {filteredResources.slice(0, 50).map((row, idx) => (
                  <tr key={idx} className="transition hover:bg-white/5" style={{ borderBottom: '1px solid rgba(100, 180, 255, 0.05)' }}>
                    {columns.slice(0, 6).map(col => (
                      <td key={col} className="px-4 py-3" style={{ color: 'rgba(148, 163, 184, 0.9)' }}>
                        <span className="block max-w-xs truncate" title={String(row[col] || '-')}>{String(row[col] || '-')}</span>
                      </td>
                    ))}
                    <td className="px-4 py-3" style={{ color: 'rgba(148, 163, 184, 0.6)' }}>{String(row._subscription || '-')}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {filteredResources.length > 50 && (
              <div className="p-3 text-center text-sm" style={{ color: 'rgba(148, 163, 184, 0.6)', background: 'rgba(30, 41, 59, 0.5)' }}>
                Showing 50 of {filteredResources.length} resources
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

// Markdown Viewer Component
function MarkdownViewer({ content, title, defaultExpanded = false }: { content: string; title?: string; defaultExpanded?: boolean }) {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded)
  const previewLength = 500
  const needsTruncation = content.length > previewLength

  return (
    <div className="rounded-xl overflow-hidden" style={glassPanel}>
      {title && (
        <div className="px-4 py-3 flex items-center justify-between" style={{ borderBottom: '1px solid rgba(100, 180, 255, 0.1)' }}>
          <div className="flex items-center space-x-3">
            <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: 'rgba(244, 114, 182, 0.2)', border: '1px solid rgba(244, 114, 182, 0.4)' }}>
              <i className="fa-solid fa-file-lines" style={{ color: '#f472b6', fontSize: '0.875rem' }}></i>
            </div>
            <span className="font-medium" style={{ color: '#e2e8f0' }}>{title}</span>
          </div>
          {needsTruncation && (
            <button
              onClick={() => setIsExpanded(!isExpanded)}
              className="text-sm font-medium transition hover:opacity-80"
              style={{ color: '#60a5fa' }}
            >
              {isExpanded ? 'Collapse' : 'Expand'}
              <i className={`fa-solid fa-chevron-${isExpanded ? 'up' : 'down'} ml-2`}></i>
            </button>
          )}
        </div>
      )}
      <div className={`px-6 py-4 prose prose-invert max-w-none ${!isExpanded && needsTruncation ? 'max-h-48 overflow-hidden relative' : ''}`}>
        <ReactMarkdown
          remarkPlugins={[remarkGfm]}
          components={{
            h1: ({ children }) => <h1 className="text-xl font-bold mb-4" style={{ color: '#e2e8f0' }}>{children}</h1>,
            h2: ({ children }) => <h2 className="text-lg font-bold mb-3 mt-6" style={{ color: '#e2e8f0' }}>{children}</h2>,
            h3: ({ children }) => <h3 className="text-base font-semibold mb-2 mt-4" style={{ color: '#e2e8f0' }}>{children}</h3>,
            p: ({ children }) => <p className="mb-3 text-sm leading-relaxed" style={{ color: 'rgba(148, 163, 184, 0.9)' }}>{children}</p>,
            ul: ({ children }) => <ul className="list-disc list-inside mb-3 space-y-1">{children}</ul>,
            ol: ({ children }) => <ol className="list-decimal list-inside mb-3 space-y-1">{children}</ol>,
            li: ({ children }) => <li className="text-sm" style={{ color: 'rgba(148, 163, 184, 0.9)' }}>{children}</li>,
            code: ({ children, className }) => {
              const isInline = !className
              return isInline ? (
                <code className="px-1.5 py-0.5 rounded text-xs" style={{ background: 'rgba(30, 41, 59, 0.8)', color: '#f472b6' }}>{children}</code>
              ) : (
                <code className="block p-3 rounded-lg text-xs overflow-x-auto" style={{ background: 'rgba(15, 23, 42, 0.8)', color: 'rgba(148, 163, 184, 0.9)' }}>{children}</code>
              )
            },
            pre: ({ children }) => <pre className="mb-3 overflow-x-auto">{children}</pre>,
            blockquote: ({ children }) => <blockquote className="border-l-4 pl-4 my-3" style={{ borderColor: '#60a5fa', color: 'rgba(148, 163, 184, 0.8)' }}>{children}</blockquote>,
            table: ({ children }) => <div className="overflow-x-auto mb-4"><table className="w-full text-sm" style={{ borderCollapse: 'collapse' }}>{children}</table></div>,
            thead: ({ children }) => <thead style={{ background: 'rgba(30, 41, 59, 0.8)' }}>{children}</thead>,
            th: ({ children }) => <th className="px-3 py-2 text-left font-semibold whitespace-nowrap" style={{ color: '#e2e8f0', border: '1px solid rgba(100, 180, 255, 0.2)' }}>{children}</th>,
            td: ({ children }) => <td className="px-3 py-2" style={{ color: 'rgba(148, 163, 184, 0.9)', border: '1px solid rgba(100, 180, 255, 0.1)' }}>{children}</td>,
            a: ({ children, href }) => <a href={href} className="underline transition hover:opacity-80" style={{ color: '#60a5fa' }} target="_blank" rel="noopener noreferrer">{children}</a>,
            strong: ({ children }) => <strong style={{ color: '#e2e8f0' }}>{children}</strong>,
          }}
        >
          {isExpanded || !needsTruncation ? content : content.slice(0, previewLength) + '...'}
        </ReactMarkdown>
        {!isExpanded && needsTruncation && (
          <div className="absolute bottom-0 left-0 right-0 h-16 bg-gradient-to-t from-slate-900/90 to-transparent pointer-events-none"></div>
        )}
      </div>
      {!isExpanded && needsTruncation && !title && (
        <div className="px-4 py-2" style={{ borderTop: '1px solid rgba(100, 180, 255, 0.1)' }}>
          <button
            onClick={() => setIsExpanded(true)}
            className="text-sm font-medium transition hover:opacity-80"
            style={{ color: '#60a5fa' }}
          >
            Read more <i className="fa-solid fa-chevron-down ml-1"></i>
          </button>
        </div>
      )}
    </div>
  )
}

// Findings Overview Section Component
function FindingsOverviewSection({ auditData }: { auditData: WafCafAuditData }) {
  const findingsFiles = auditData.findings.files

  if (findingsFiles.length === 0) return null

  // Find the CURRENT_STATE_ANALYSIS.md file
  const analysisFile = findingsFiles.find(f => f.name.toLowerCase().includes('current_state_analysis') || f.name.toLowerCase().includes('current-state-analysis'))
  const otherFindingsFiles = findingsFiles.filter(f => f !== analysisFile)

  return (
    <div className="space-y-4">
      {/* Current State Analysis Markdown - with integrated header */}
      {analysisFile && typeof analysisFile.data === 'string' && (
        <MarkdownViewer
          content={analysisFile.data}
          title="Findings Overview"
          defaultExpanded={false}
        />
      )}

      {/* Compact metrics for other findings files */}
      {otherFindingsFiles.length > 0 && (
        <div className="flex flex-wrap gap-3">
          {otherFindingsFiles.map((file, idx) => {
            const style = categoryStyles[file.category] || categoryStyles.other
            return (
              <div
                key={`${file.path}-${idx}`}
                className="inline-flex items-center space-x-2 px-3 py-1.5 rounded-lg"
                style={{ background: `${style.color}15`, border: `1px solid ${style.color}30` }}
                title={file.name}
              >
                <i className={`fa-solid ${style.icon} text-xs`} style={{ color: style.color }}></i>
                <span className="text-xs font-medium" style={{ color: style.color }}>{style.label}</span>
                <span className="text-xs font-bold" style={{ color: '#e2e8f0' }}>{file.row_count}</span>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}

// Data Table Component
function DataTable({ file }: { file: WafCafAuditFile }) {
  const [expanded, setExpanded] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')

  if (!file.data || (Array.isArray(file.data) && file.data.length === 0)) {
    return null
  }

  // Handle markdown files - render with MarkdownViewer
  if (typeof file.data === 'string') {
    return <MarkdownViewer content={file.data} defaultExpanded={true} />
  }

  const isArray = Array.isArray(file.data)
  const rows: RowData[] = isArray ? (file.data as RowData[]) : [file.data as RowData]
  const columns = rows.length > 0 ? Object.keys(rows[0]) : []

  const filteredRows = searchTerm
    ? rows.filter((row: RowData) => {
        const rowValues = Object.values(row)
        return rowValues.some((val) => String(val).toLowerCase().includes(searchTerm.toLowerCase()))
      })
    : rows

  const displayRows = expanded ? filteredRows : filteredRows.slice(0, 10)
  const hasMore = filteredRows.length > 10

  const formatValue = (value: unknown): string => {
    if (value === null || value === undefined) return '-'
    if (typeof value === 'object') return JSON.stringify(value)
    return String(value)
  }

  const getSeverityStyle = (col: string, value: unknown) => {
    if (col.toLowerCase() === 'severity' && typeof value === 'string') {
      const color = severityColors[value] || 'rgba(148, 163, 184, 0.9)'
      return { color, fontWeight: 600 }
    }
    return { color: 'rgba(148, 163, 184, 0.9)' }
  }

  return (
    <div className="overflow-hidden rounded-lg" style={{ border: '1px solid rgba(100, 180, 255, 0.1)' }}>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr style={{ background: 'rgba(30, 41, 59, 0.8)' }}>
              {columns.map((col) => (
                <th key={col} className="px-4 py-3 text-left font-semibold whitespace-nowrap" style={{ color: '#e2e8f0', borderBottom: '1px solid rgba(100, 180, 255, 0.1)' }}>
                  {col}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {displayRows.map((row: RowData, idx: number) => (
              <tr key={idx} className="transition hover:bg-white/5" style={{ borderBottom: idx < displayRows.length - 1 ? '1px solid rgba(100, 180, 255, 0.05)' : 'none' }}>
                {columns.map((col) => (
                  <td key={col} className="px-4 py-3" style={getSeverityStyle(col, row[col])}>
                    <span className="block max-w-xs truncate" title={formatValue(row[col])}>{formatValue(row[col])}</span>
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {(hasMore || searchTerm) && (
        <div className="px-4 py-3 flex items-center justify-between" style={{ background: 'rgba(30, 41, 59, 0.5)', borderTop: '1px solid rgba(100, 180, 255, 0.1)' }}>
          <div className="flex items-center space-x-2">
            <input type="text" placeholder="Search..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="px-3 py-1.5 text-sm rounded-lg" style={{ background: 'rgba(15, 23, 42, 0.6)', border: '1px solid rgba(100, 180, 255, 0.2)', color: '#e2e8f0' }} />
            <span className="text-xs" style={{ color: 'rgba(148, 163, 184, 0.6)' }}>{filteredRows.length} of {rows.length} rows</span>
          </div>
          {hasMore && (
            <button onClick={() => setExpanded(!expanded)} className="text-sm font-medium transition hover:opacity-80" style={{ color: '#60a5fa' }}>
              {expanded ? 'Show Less' : `Show All ${filteredRows.length} Rows`}
            </button>
          )}
        </div>
      )}
    </div>
  )
}

// File Section Component
function FileSection({ file, defaultOpen = false }: { file: WafCafAuditFile; defaultOpen?: boolean }) {
  const [isOpen, setIsOpen] = useState(defaultOpen)
  const style = categoryStyles[file.category] || categoryStyles.other

  return (
    <div className="rounded-xl overflow-hidden" style={glassPanel}>
      <button onClick={() => setIsOpen(!isOpen)} className="w-full px-4 py-3 flex items-center justify-between transition hover:bg-white/5">
        <div className="flex items-center space-x-3">
          <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: `${style.color}20`, border: `1px solid ${style.color}40` }}>
            <i className={`fa-solid ${style.icon}`} style={{ color: style.color, fontSize: '0.875rem' }}></i>
          </div>
          <div className="text-left">
            <p className="font-medium" style={{ color: '#e2e8f0' }}>{file.name}</p>
            <p className="text-xs" style={{ color: 'rgba(148, 163, 184, 0.6)' }}>
              {file.type === 'md' ? 'Markdown document' : `${file.row_count} rows`} • {file.type.toUpperCase()} • {file.scope_name || file.subscription}
            </p>
          </div>
        </div>
        <i className={`fa-solid fa-chevron-${isOpen ? 'up' : 'down'}`} style={{ color: 'rgba(148, 163, 184, 0.6)' }}></i>
      </button>
      {isOpen && (
        <div className="px-4 pb-4">
          <DataTable file={file} />
        </div>
      )}
    </div>
  )
}

// Category Section Component
function CategorySection({ category, files, defaultExpanded = true }: { category: string; files: WafCafAuditFile[]; defaultExpanded?: boolean }) {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded)
  const style = categoryStyles[category] || categoryStyles.other
  const totalRows = files.reduce((sum, f) => sum + f.row_count, 0)

  return (
    <div className="space-y-3">
      <button onClick={() => setIsExpanded(!isExpanded)} className="w-full flex items-center justify-between py-2">
        <div className="flex items-center space-x-3">
          <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ background: `${style.color}20`, border: `1px solid ${style.color}40` }}>
            <i className={`fa-solid ${style.icon} text-lg`} style={{ color: style.color }}></i>
          </div>
          <div className="text-left">
            <h3 className="text-lg font-semibold" style={{ color: '#e2e8f0' }}>{style.label}</h3>
            <p className="text-sm" style={{ color: 'rgba(148, 163, 184, 0.6)' }}>{files.length} files • {totalRows.toLocaleString()} total rows</p>
          </div>
        </div>
        <i className={`fa-solid fa-chevron-${isExpanded ? 'up' : 'down'}`} style={{ color: 'rgba(148, 163, 184, 0.6)' }}></i>
      </button>
      {isExpanded && (
        <div className="space-y-3 pl-13">
          {files.map((file, idx) => (
            <FileSection key={`${file.path}-${idx}`} file={file} />
          ))}
        </div>
      )}
    </div>
  )
}

export default function AuditReports({ presentationMode: presentationModeProp }: { presentationMode?: boolean } = {}) {
  const { projectId } = useParams<{ projectId: string }>()
  const location = useLocation()
  const isPresentation = presentationModeProp || location.pathname.endsWith('/present')
  const [selectedAuditId, setSelectedAuditId] = useState<string | null>(null)
  const [selectedSubscription, setSelectedSubscription] = useState<string>('all')
  const [viewMode, setViewMode] = useState<'dashboard' | 'tables' | 'backlog'>('dashboard')
  const [drillDown, setDrillDown] = useState<DrillDownState>({ type: null, value: null })
  const [globalSearchTerm, setGlobalSearchTerm] = useState('')

  const { data: project } = useQuery({
    queryKey: ['project', projectId],
    queryFn: () => getProject(projectId!).then((res) => res.data),
    enabled: !!projectId,
  })

  const { data: audits, isLoading: auditsLoading } = useQuery({
    queryKey: ['wafCafAudits', projectId],
    queryFn: () => getWafCafAudits(projectId!).then((res) => res.data),
    enabled: !!projectId,
  })

  const indexedAudits = audits?.filter((a: WafCafAudit) => a.status === 'indexed') || []

  // Auto-select audit: if only one, select it; if multiple, select the latest (first in list)
  useEffect(() => {
    if (!selectedAuditId && indexedAudits.length > 0) {
      // Select the first audit (latest based on upload order)
      setSelectedAuditId(indexedAudits[0].id)
    }
  }, [indexedAudits, selectedAuditId])

  const { data: auditData, isLoading: dataLoading, error: dataError } = useQuery({
    queryKey: ['wafCafAuditData', selectedAuditId],
    queryFn: () => getWafCafAuditData(selectedAuditId!).then((res) => res.data),
    enabled: !!selectedAuditId,
  })

  // Process data for visualizations
  const visualizationData = useMemo(() => {
    if (!auditData) return null

    // Use all files
    let files: WafCafAuditFile[] = auditData.files

    // Apply subscription filter if set
    if (selectedSubscription !== 'all') {
      files = files.filter((f: WafCafAuditFile) => f.subscription === selectedSubscription)
    }

    // Extract findings
    const allFindings: Finding[] = []
    const severityCounts: Record<string, number> = {}
    const resourceTypeCounts: Record<string, number> = {}
    const wafScores: Record<string, number> = {}

    files.forEach((file: WafCafAuditFile) => {
      // Skip markdown files for data extraction
      if (typeof file.data === 'string') return

      const rows = Array.isArray(file.data) ? file.data : [file.data]

      // Process findings files
      if (file.name.includes('findings') || file.name.includes('waf-score')) {
        rows.forEach((row: RowData) => {
          const severity = row['Severity'] as string
          if (severity) {
            severityCounts[severity] = (severityCounts[severity] || 0) + 1
            allFindings.push({
              category: file.category,
              resourceName: String(row['ResourceName'] || row['Resource'] || row['Name'] || 'Unknown'),
              finding: String(row['Finding'] || row['Description'] || row['CheckName'] || ''),
              severity,
              recommendation: String(row['Recommendation'] || row['Solution'] || ''),
              source: file.name,
              subscription: file.subscription,
            })
          }
        })
      }

      // Process resource type files
      if (file.name.includes('resource-type') || file.name.includes('resource-inventory')) {
        rows.forEach((row: RowData) => {
          const resourceType = (row['ResourceType'] || row['Type']) as string
          const count = parseInt(String(row['Count'] || 1), 10)
          if (resourceType) {
            resourceTypeCounts[resourceType] = (resourceTypeCounts[resourceType] || 0) + count
          }
        })
      }

      // Process subscription scores
      if (file.name.includes('subscription-scores') || file.name.includes('waf-score-summary')) {
        rows.forEach((row: RowData) => {
          ['Security', 'Reliability', 'CostOptimization', 'OperationalExcellence', 'Performance'].forEach(pillar => {
            const score = row[pillar]
            if (score !== undefined && score !== null) {
              const numScore = parseInt(String(score), 10)
              if (!isNaN(numScore)) {
                wafScores[pillar] = Math.max(wafScores[pillar] || 0, numScore)
              }
            }
          })
          if (row['Overall'] !== undefined) {
            wafScores['Overall'] = parseInt(String(row['Overall']), 10)
          }
        })
      }
    })

    const findingsBySeverity = ['Critical', 'High', 'Medium', 'Low', 'Info']
      .map(sev => ({ severity: sev, count: severityCounts[sev] || 0 }))
      .filter(f => f.count > 0)

    const resourceTypes = Object.entries(resourceTypeCounts)
      .map(([type, count]) => ({ type: type.split('/').pop() || type, count }))
      .sort((a, b) => b.count - a.count)

    const wafPillarScores = ['Security', 'Reliability', 'CostOptimization', 'OperationalExcellence', 'Performance']
      .map(pillar => ({
        pillar: pillar === 'CostOptimization' ? 'Cost Opt.' : pillar === 'OperationalExcellence' ? 'Ops Exc.' : pillar,
        score: wafScores[pillar] || 0,
        fullMark: 100
      }))

    return {
      findingsBySeverity,
      resourceTypes,
      wafPillarScores,
      wafScores,
      files,
      allFindings
    }
  }, [auditData, selectedSubscription])

  // Dashboard search filtering
  const dashboardFilteredData = useMemo(() => {
    if (!visualizationData || !globalSearchTerm) return visualizationData
    const term = globalSearchTerm.toLowerCase()
    const filtered = visualizationData.allFindings.filter(f =>
      f.finding.toLowerCase().includes(term) ||
      f.resourceName.toLowerCase().includes(term) ||
      f.category.toLowerCase().includes(term) ||
      f.severity.toLowerCase().includes(term) ||
      f.recommendation.toLowerCase().includes(term)
    )
    const severityCounts: Record<string, number> = {}
    filtered.forEach(f => {
      severityCounts[f.severity] = (severityCounts[f.severity] || 0) + 1
    })
    const findingsBySeverity = ['Critical', 'High', 'Medium', 'Low', 'Info']
      .map(sev => ({ severity: sev, count: severityCounts[sev] || 0 }))
      .filter(f => f.count > 0)
    return {
      ...visualizationData,
      allFindings: filtered,
      findingsBySeverity,
    }
  }, [visualizationData, globalSearchTerm])

  // Group files by category
  const categorizedFiles: Record<string, WafCafAuditFile[]> = {}
  const filteredFiles = visualizationData?.files || []
  filteredFiles.forEach((file: WafCafAuditFile) => {
    if (!categorizedFiles[file.category]) {
      categorizedFiles[file.category] = []
    }
    categorizedFiles[file.category].push(file)
  })

  const categoryOrder = ['advisor', 'security', 'identity', 'network', 'governance', 'cost', 'monitoring', 'disaster_recovery', 'compute', 'paas', 'subscriptions', 'other']
  const sortedCategories = Object.keys(categorizedFiles).sort((a, b) => categoryOrder.indexOf(a) - categoryOrder.indexOf(b))

  // Handle drill-down navigation
  const handleSeverityClick = (severity: string) => {
    setDrillDown({ type: 'severity', value: severity })
  }

  const handleCategoryClick = (category: string) => {
    setDrillDown({ type: 'category', value: category })
  }

  const handleResourceClick = (resourceType: string) => {
    setDrillDown({ type: 'resource', value: resourceType })
  }

  const handleBackFromDrillDown = () => {
    setDrillDown({ type: null, value: null })
  }

  // Render drill-down views
  if (drillDown.type === 'severity' && visualizationData) {
    return (
      <div className={`space-y-6${isPresentation ? ' min-h-screen p-6' : ''}`} style={isPresentation ? { background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)' } : undefined}>
        <div>
          {!isPresentation && (
          <Link to={`/projects/${projectId}`} className="inline-flex items-center text-sm font-medium mb-4 transition hover:opacity-80" style={{ color: '#60a5fa' }}>
            <i className="fa-solid fa-arrow-left mr-2"></i>Back to Project
          </Link>
          )}
        </div>
        <FindingsDetailView
          findings={visualizationData.allFindings}
          severityFilter={drillDown.value}
          projectId={projectId!}
          auditId={selectedAuditId!}
          onBack={handleBackFromDrillDown}
          onClearFilter={() => setDrillDown({ ...drillDown, value: null })}
        />
      </div>
    )
  }

  if (drillDown.type === 'resource' && visualizationData && drillDown.value) {
    return (
      <div className={`space-y-6${isPresentation ? ' min-h-screen p-6' : ''}`} style={isPresentation ? { background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)' } : undefined}>
        <div>
          {!isPresentation && (
          <Link to={`/projects/${projectId}`} className="inline-flex items-center text-sm font-medium mb-4 transition hover:opacity-80" style={{ color: '#60a5fa' }}>
            <i className="fa-solid fa-arrow-left mr-2"></i>Back to Project
          </Link>
          )}
        </div>
        <ResourceDetailView
          files={visualizationData.files}
          resourceType={drillDown.value}
          onBack={handleBackFromDrillDown}
        />
      </div>
    )
  }

  if (drillDown.type === 'category' && drillDown.value) {
    const categoryFiles = categorizedFiles[drillDown.value] || []
    const style = categoryStyles[drillDown.value] || categoryStyles.other

    return (
      <div className={`space-y-6${isPresentation ? ' min-h-screen p-6' : ''}`} style={isPresentation ? { background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)' } : undefined}>
        <div>
          {!isPresentation && (
          <Link to={`/projects/${projectId}`} className="inline-flex items-center text-sm font-medium mb-4 transition hover:opacity-80" style={{ color: '#60a5fa' }}>
            <i className="fa-solid fa-arrow-left mr-2"></i>Back to Project
          </Link>
          )}
        </div>
        <div className="flex items-center space-x-3">
          <button onClick={handleBackFromDrillDown} className="p-2 rounded-lg transition hover:bg-white/10" style={{ color: '#60a5fa' }}>
            <i className="fa-solid fa-arrow-left"></i>
          </button>
          <div className="w-12 h-12 rounded-lg flex items-center justify-center" style={{ background: `${style.color}20`, border: `1px solid ${style.color}40` }}>
            <i className={`fa-solid ${style.icon} text-xl`} style={{ color: style.color }}></i>
          </div>
          <div>
            <h2 className="text-xl font-bold" style={{ color: '#e2e8f0' }}>{style.label}</h2>
            <p className="text-sm" style={{ color: 'rgba(148, 163, 184, 0.6)' }}>{categoryFiles.length} files</p>
          </div>
        </div>
        <div className="space-y-3">
          {categoryFiles.map((file, idx) => (
            <FileSection key={`${file.path}-${idx}`} file={file} defaultOpen={idx === 0} />
          ))}
        </div>
      </div>
    )
  }

  return (
    <div className={`space-y-6${isPresentation ? ' min-h-screen p-6' : ''}`} style={isPresentation ? { background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)' } : undefined}>
      {/* Header */}
      <div>
        {isPresentation ? (
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center space-x-4">
              <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ background: 'rgba(96, 165, 250, 0.2)', border: '1px solid rgba(96, 165, 250, 0.3)' }}>
                <i className="fa-solid fa-chart-bar text-lg" style={{ color: '#60a5fa' }}></i>
              </div>
              <div>
                <h1 className="text-2xl font-bold" style={{ color: '#e2e8f0' }}>Audit Report</h1>
                <p className="text-sm" style={{ color: 'rgba(148, 163, 184, 0.7)' }}>{project?.project_name}</p>
              </div>
            </div>
            <button
              onClick={() => window.close()}
              className="px-4 py-2 rounded-lg text-sm font-medium transition hover:bg-white/10"
              style={{ color: 'rgba(148, 163, 184, 0.8)', border: '1px solid rgba(100, 180, 255, 0.2)' }}
            >
              <i className="fa-solid fa-times mr-2"></i>Close
            </button>
          </div>
        ) : (
        <>
        <Link to={`/projects/${projectId}`} className="inline-flex items-center text-sm font-medium mb-4 transition hover:opacity-80" style={{ color: '#60a5fa' }}>
          <i className="fa-solid fa-arrow-left mr-2"></i>Back to Project
        </Link>
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold" style={{ color: '#e2e8f0' }}>Audit Reports</h1>
            <p style={{ color: 'rgba(148, 163, 184, 0.7)' }}>{project?.project_name} - Azure audit insights and data</p>
          </div>
          <div className="flex items-center gap-3">
            {/* Audit Selector Dropdown - only show if multiple audits */}
            {!auditsLoading && indexedAudits.length > 1 && (
              <select
                value={selectedAuditId || ''}
                onChange={(e) => setSelectedAuditId(e.target.value)}
                className="px-3 py-2 rounded-lg text-sm font-medium"
                style={{ background: 'rgba(30, 41, 59, 0.8)', border: '1px solid rgba(100, 180, 255, 0.2)', color: '#e2e8f0' }}
              >
                {indexedAudits.map((audit: WafCafAudit) => (
                  <option key={audit.id} value={audit.id}>
                    {audit.original_filename} ({audit.file_count} files)
                  </option>
                ))}
              </select>
            )}
            {/* Show current audit name if only one */}
            {!auditsLoading && indexedAudits.length === 1 && selectedAuditId && (
              <span className="text-sm px-3 py-2 rounded-lg" style={{ background: 'rgba(30, 41, 59, 0.6)', border: '1px solid rgba(100, 180, 255, 0.1)', color: 'rgba(148, 163, 184, 0.8)' }}>
                <i className="fa-solid fa-file-zipper mr-2" style={{ color: '#60a5fa' }}></i>
                {indexedAudits[0]?.original_filename}
              </span>
            )}
            <Link to={`/projects/${projectId}/waf-caf-audit`} className="px-4 py-2 rounded-lg text-sm font-medium transition inline-flex items-center space-x-2" style={{ background: 'rgba(20, 184, 166, 0.2)', border: '1px solid rgba(20, 184, 166, 0.4)', color: '#14b8a6' }}>
              <i className="fa-solid fa-upload"></i>
              <span>Upload Audit</span>
            </Link>
          </div>
        </div>
        </>
        )}
      </div>

      {/* No Audits State */}
      {!auditsLoading && indexedAudits.length === 0 && (
        <div className="rounded-xl p-8 text-center" style={glassPanel}>
          <div className="w-16 h-16 rounded-xl flex items-center justify-center mx-auto mb-4" style={{ background: 'rgba(100, 116, 139, 0.2)', border: '1px solid rgba(100, 180, 255, 0.1)' }}>
            <i className="fa-solid fa-file-zipper text-3xl" style={{ color: 'rgba(148, 163, 184, 0.5)' }}></i>
          </div>
          <p className="font-semibold mb-2" style={{ color: '#e2e8f0' }}>No indexed audits available</p>
          <p className="text-sm mb-4" style={{ color: 'rgba(148, 163, 184, 0.7)' }}>Upload a WAF-CAF audit zip file to view reports</p>
          {!isPresentation && (
          <Link to={`/projects/${projectId}/waf-caf-audit`} className="px-4 py-2 rounded-lg text-sm font-medium transition inline-flex items-center space-x-2" style={{ background: 'rgba(20, 184, 166, 0.2)', border: '1px solid rgba(20, 184, 166, 0.4)', color: '#14b8a6' }}>
            <i className="fa-solid fa-upload"></i>
            <span>Upload Audit</span>
          </Link>
          )}
        </div>
      )}

      {/* Data Display */}
      {selectedAuditId && (
        <>
          {dataLoading ? (
            <div className="rounded-xl p-8 text-center" style={glassPanel}>
              <i className="fa-solid fa-spinner fa-spin text-4xl mb-4" style={{ color: '#60a5fa' }}></i>
              <p style={{ color: 'rgba(148, 163, 184, 0.8)' }}>Loading audit data...</p>
            </div>
          ) : dataError ? (
            <div className="rounded-xl p-8 text-center" style={glassPanel}>
              <div className="w-16 h-16 rounded-xl flex items-center justify-center mx-auto mb-4" style={{ background: 'rgba(248, 113, 113, 0.2)', border: '1px solid rgba(248, 113, 113, 0.3)' }}>
                <i className="fa-solid fa-exclamation-triangle text-3xl" style={{ color: '#f87171' }}></i>
              </div>
              <p className="font-semibold mb-2" style={{ color: '#f87171' }}>Failed to load audit data</p>
              <p className="text-sm" style={{ color: 'rgba(148, 163, 184, 0.7)' }}>Please try again or select a different audit</p>
            </div>
          ) : auditData && visualizationData ? (
            <>
              {/* Controls Bar */}
              <div className="rounded-xl p-4" style={glassPanel}>
                <div className="flex flex-wrap items-center gap-4">
                  {/* Stats */}
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ background: 'rgba(96, 165, 250, 0.15)', border: '1px solid rgba(96, 165, 250, 0.3)' }}>
                      <i className="fa-solid fa-file-alt text-lg" style={{ color: '#60a5fa' }}></i>
                    </div>
                    <div>
                      <p className="text-xl font-bold" style={{ color: '#e2e8f0' }}>{auditData.stats.total_files}</p>
                      <p className="text-xs" style={{ color: 'rgba(148, 163, 184, 0.6)' }}>Files</p>
                    </div>
                  </div>
                  {auditData.stats.findings_count > 0 && (
                    <div className="flex items-center space-x-3">
                      <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ background: 'rgba(244, 114, 182, 0.15)', border: '1px solid rgba(244, 114, 182, 0.3)' }}>
                        <i className="fa-solid fa-clipboard-list text-lg" style={{ color: '#f472b6' }}></i>
                      </div>
                      <div>
                        <p className="text-xl font-bold" style={{ color: '#e2e8f0' }}>{auditData.stats.findings_count}</p>
                        <p className="text-xs" style={{ color: 'rgba(148, 163, 184, 0.6)' }}>Findings Files</p>
                      </div>
                    </div>
                  )}
                  {auditData.stats.management_group_count > 0 && (
                    <div className="flex items-center space-x-3">
                      <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ background: 'rgba(167, 139, 250, 0.15)', border: '1px solid rgba(167, 139, 250, 0.3)' }}>
                        <i className="fa-solid fa-sitemap text-lg" style={{ color: '#a78bfa' }}></i>
                      </div>
                      <div>
                        <p className="text-xl font-bold" style={{ color: '#e2e8f0' }}>{auditData.stats.management_group_count}</p>
                        <p className="text-xs" style={{ color: 'rgba(148, 163, 184, 0.6)' }}>Mgmt Groups</p>
                      </div>
                    </div>
                  )}
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ background: 'rgba(52, 211, 153, 0.15)', border: '1px solid rgba(52, 211, 153, 0.3)' }}>
                      <i className="fa-solid fa-layer-group text-lg" style={{ color: '#34d399' }}></i>
                    </div>
                    <div>
                      <p className="text-xl font-bold" style={{ color: '#e2e8f0' }}>{auditData.stats.subscription_count}</p>
                      <p className="text-xs" style={{ color: 'rgba(148, 163, 184, 0.6)' }}>Subscriptions</p>
                    </div>
                  </div>
                  {visualizationData.allFindings.length > 0 && (
                    <button
                      onClick={() => setDrillDown({ type: 'severity', value: null })}
                      className="flex items-center space-x-3 px-3 py-2 rounded-lg transition hover:bg-white/5"
                    >
                      <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ background: 'rgba(251, 191, 36, 0.15)', border: '1px solid rgba(251, 191, 36, 0.3)' }}>
                        <i className="fa-solid fa-exclamation-triangle text-lg" style={{ color: '#fbbf24' }}></i>
                      </div>
                      <div className="text-left">
                        <p className="text-xl font-bold" style={{ color: '#e2e8f0' }}>{visualizationData.allFindings.length}</p>
                        <p className="text-xs" style={{ color: 'rgba(148, 163, 184, 0.6)' }}>Issues Found</p>
                      </div>
                      <i className="fa-solid fa-chevron-right" style={{ color: 'rgba(148, 163, 184, 0.4)' }}></i>
                    </button>
                  )}

                  <div className="ml-auto flex items-center gap-3">
                    {/* Global Search */}
                    <div className="relative">
                      <i className="fa-solid fa-search absolute left-3 top-1/2 -translate-y-1/2" style={{ color: 'rgba(148, 163, 184, 0.4)', fontSize: '0.75rem' }}></i>
                      <input
                        type="text"
                        placeholder="Search findings, IDs..."
                        value={globalSearchTerm}
                        onChange={(e) => setGlobalSearchTerm(e.target.value)}
                        className="pl-8 pr-3 py-2 rounded-lg text-sm w-56"
                        style={{ background: 'rgba(15, 23, 42, 0.6)', border: '1px solid rgba(100, 180, 255, 0.2)', color: '#e2e8f0' }}
                      />
                      {globalSearchTerm && (
                        <button
                          onClick={() => setGlobalSearchTerm('')}
                          className="absolute right-2 top-1/2 -translate-y-1/2 p-0.5 rounded hover:bg-white/10"
                          style={{ color: 'rgba(148, 163, 184, 0.6)' }}
                        >
                          <i className="fa-solid fa-times text-xs"></i>
                        </button>
                      )}
                    </div>

                    {/* View Mode Toggle */}
                    <div className="flex rounded-lg overflow-hidden" style={{ border: '1px solid rgba(100, 180, 255, 0.2)' }}>
                      <button
                        onClick={() => setViewMode('dashboard')}
                        className="px-4 py-2 text-sm font-medium transition"
                        style={{
                          background: viewMode === 'dashboard' ? 'rgba(96, 165, 250, 0.2)' : 'transparent',
                          color: viewMode === 'dashboard' ? '#60a5fa' : 'rgba(148, 163, 184, 0.7)'
                        }}
                      >
                        <i className="fa-solid fa-chart-pie mr-2"></i>Dashboard
                      </button>
                      <button
                        onClick={() => setViewMode('tables')}
                        className="px-4 py-2 text-sm font-medium transition"
                        style={{
                          background: viewMode === 'tables' ? 'rgba(96, 165, 250, 0.2)' : 'transparent',
                          color: viewMode === 'tables' ? '#60a5fa' : 'rgba(148, 163, 184, 0.7)'
                        }}
                      >
                        <i className="fa-solid fa-table mr-2"></i>Tables
                      </button>
                      <button
                        onClick={() => setViewMode('backlog')}
                        className="px-4 py-2 text-sm font-medium transition"
                        style={{
                          background: viewMode === 'backlog' ? 'rgba(167, 139, 250, 0.2)' : 'transparent',
                          color: viewMode === 'backlog' ? '#a78bfa' : 'rgba(148, 163, 184, 0.7)'
                        }}
                      >
                        <i className="fa-solid fa-clipboard-list mr-2"></i>Backlog
                      </button>
                    </div>

                    {/* Present Button */}
                    {!isPresentation && (
                      <button
                        onClick={() => window.open(`/projects/${projectId}/audit-reports/present`, '_blank')}
                        className="px-4 py-2 rounded-lg text-sm font-medium transition hover:scale-105"
                        style={{ background: 'rgba(139, 92, 246, 0.2)', border: '1px solid rgba(139, 92, 246, 0.4)', color: '#8b5cf6' }}
                        title="Open in presentation mode (no sidebar)"
                      >
                        <i className="fa-solid fa-display mr-2"></i>Present
                      </button>
                    )}

                    {/* Legacy Subscription Filter */}
                    <select
                      value={selectedSubscription}
                      onChange={(e) => setSelectedSubscription(e.target.value)}
                      className="px-4 py-2 rounded-lg text-sm"
                      style={{ background: 'rgba(30, 41, 59, 0.8)', border: '1px solid rgba(100, 180, 255, 0.2)', color: '#e2e8f0' }}
                    >
                      <option value="all">All Subscriptions</option>
                      {Object.keys(auditData.subscriptions).map((sub) => (
                        <option key={sub} value={sub}>{sub}</option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>

              {/* Main Content */}
              <div className="space-y-6">
              {viewMode === 'dashboard' ? (
                <>
                  {/* Findings Overview Section - Show prominently at top */}
                  {auditData.findings.files.length > 0 && (
                    <FindingsOverviewSection auditData={auditData} />
                  )}

                  {/* WAF Score Cards */}
                  {Object.keys(visualizationData.wafScores).length > 0 && (
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
                      {visualizationData.wafScores['Overall'] && (
                        <ScoreCard title="Overall" score={visualizationData.wafScores['Overall']} color="#60a5fa" icon="fa-gauge-high" />
                      )}
                      {visualizationData.wafPillarScores.filter(p => p.score > 0).map((pillar) => (
                        <ScoreCard
                          key={pillar.pillar}
                          title={pillar.pillar}
                          score={pillar.score}
                          color={pillarColors[pillar.pillar.replace(' ', '')] || '#60a5fa'}
                          icon={pillar.pillar === 'Security' ? 'fa-shield-halved' : pillar.pillar === 'Reliability' ? 'fa-server' : pillar.pillar.includes('Cost') ? 'fa-dollar-sign' : pillar.pillar.includes('Ops') ? 'fa-gears' : 'fa-bolt'}
                        />
                      ))}
                    </div>
                  )}

                  {/* Search results indicator */}
                  {globalSearchTerm && dashboardFilteredData && (
                    <div className="rounded-lg px-4 py-2 flex items-center gap-2" style={{ background: 'rgba(96, 165, 250, 0.1)', border: '1px solid rgba(96, 165, 250, 0.2)' }}>
                      <i className="fa-solid fa-filter text-xs" style={{ color: '#60a5fa' }}></i>
                      <span className="text-sm" style={{ color: '#60a5fa' }}>
                        Showing {dashboardFilteredData.allFindings.length} of {visualizationData.allFindings.length} findings matching "{globalSearchTerm}"
                      </span>
                    </div>
                  )}

                  {/* Charts Row - Findings and Resources side by side */}
                  <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    {(dashboardFilteredData || visualizationData).findingsBySeverity.length > 0 && (
                      <div className="lg:col-span-1">
                        <FindingsSummary findings={(dashboardFilteredData || visualizationData).findingsBySeverity} onSeverityClick={handleSeverityClick} />
                      </div>
                    )}
                    {visualizationData.resourceTypes.length > 0 && (
                      <div className="lg:col-span-2">
                        <ResourceTypeChart data={visualizationData.resourceTypes} onResourceClick={handleResourceClick} />
                      </div>
                    )}
                  </div>

                  {/* WAF Radar Chart - if available */}
                  {visualizationData.wafPillarScores.some(p => p.score > 0) && (
                    <WafRadarChart scores={visualizationData.wafPillarScores} />
                  )}

                  {/* Category Overview Cards */}
                  <div>
                    <h2 className="text-lg font-bold mb-4" style={{ color: '#e2e8f0' }}>Categories Overview</h2>
                    <p className="text-xs mb-4" style={{ color: 'rgba(148, 163, 184, 0.5)' }}>Click on a category to explore its data</p>
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-4">
                      {sortedCategories.map((category) => (
                        <CategoryStatsCard
                          key={category}
                          category={category}
                          files={categorizedFiles[category]}
                          color={categoryStyles[category]?.color || '#94a3b8'}
                          icon={categoryStyles[category]?.icon || 'fa-file-alt'}
                          onClick={() => handleCategoryClick(category)}
                        />
                      ))}
                    </div>
                  </div>
                </>
              ) : viewMode === 'tables' ? (
                /* Tables View */
                <div className="space-y-6">
                  {sortedCategories.map((category) => (
                    <CategorySection key={category} category={category} files={categorizedFiles[category]} />
                  ))}
                </div>
              ) : (
                /* Backlog View */
                <BacklogPanel projectId={projectId!} auditId={selectedAuditId!} searchTerm={globalSearchTerm || undefined} presentationMode={isPresentation} />
              )}
              </div>
            </>
          ) : null}
        </>
      )}

    </div>
  )
}
